import React, { FC, useEffect, useState } from 'react';
import { App, ConfigProvider } from 'antd';

import { antdTheme } from '../../../clients/shared/theme';
import { getCurrentProjectName, getCurrentProjectUrl } from 'models/Project';
import {
  getCurrentProjectId,
  getCurrentOrganizationId,
  getCurrentUserId
} from 'models/Organization';

import Cards from './Cards';
import Onboarding from './Onboarding';
import { KanbanProps as Props } from './types';
import ProjectTour from './ProjectTour';

import styles from '../../../clients/shared/overrides.module.css';
import { CurrentUserProvider } from '../../../../clients/providers/CurrentUser';
import { Provider } from 'react-redux';

const Kanban: FC<Props> = props => {
  const {
    showOnboarding,
    isJavascriptInstalled,
    kanbanLoading,
    onboardingModalDismissed,
    isExtensionInstalled,
    isTaskCreated,
    experiments,
    closeTask,
    setActiveTask,
    isGuest,
    viewGuestKanban,
    currentUserId,
    bugherdUrl,
    apiKey,
    javascriptInstalledOverride,
    hasInvitedMember,
    skippedInviteMember
  } = props;

  const [isLegacyOnboardingComplete, setLegacyOnboardingComplete] = useState(
    false
  );

  const completedOnboarding =
    !showOnboarding ||
    localStorage.getItem(`${currentUserId}_finished_project_onboarding`);
  // Q2 2026 onboarding variant runs its own standalone wizard
  // (/welcome-to-bugherd) and hands off to the kanban board for the tour.
  // Suppress the legacy onboarding modal for these users so they don't get a
  // second onboarding flow — the ProjectTour below still runs.
  const isQ22026OnboardingVariant = experiments.includes(
    'q22026_onboarding_variant'
  );
  const showOnboardingModal =
    !isGuest &&
    !kanbanLoading &&
    !(completedOnboarding || isLegacyOnboardingComplete) &&
    !onboardingModalDismissed &&
    !isQ22026OnboardingVariant;
  const showProjectTour =
    !isGuest &&
    !kanbanLoading &&
    !completedOnboarding &&
    !showOnboardingModal &&
    !onboardingModalDismissed;

  useEffect(() => {
    if (localStorage.getItem(`${currentUserId}_finished_onboarding_modal`)) {
      setLegacyOnboardingComplete(true);
    }
  }, []);

  const handleOnCloseModal = () => {
    setLegacyOnboardingComplete(true);
    localStorage.setItem(`${currentUserId}_finished_onboarding_modal`, 'true');
  };

  return (
    <CurrentUserProvider
      apiDomain={''}
      projectId={Number(getCurrentProjectId())}
    >
      <ConfigProvider theme={antdTheme}>
        <Provider store={window.bugherdStore}>
          <App className={styles.antdOverrides}>
            {(!isGuest || viewGuestKanban) && <Cards {...props} />}
            {/* Project tour for legacy & new onboarding */}
            {showProjectTour && (
              <ProjectTour
                projectId={getCurrentProjectId()}
                closeTask={closeTask}
                setActiveTask={setActiveTask}
                currentUserId={currentUserId}
              />
            )}
            {/* Legacy onboarding modal */}
            {showOnboardingModal && (
              <Onboarding
                showOnboarding={!completedOnboarding}
                isWebsiteVisited={isTaskCreated}
                isJavascriptInstalled={isJavascriptInstalled}
                projectName={getCurrentProjectName()}
                projectUrl={getCurrentProjectUrl()}
                projectId={getCurrentProjectId()}
                userId={getCurrentUserId()}
                organizationId={getCurrentOrganizationId()}
                isExtensionInstalled={isExtensionInstalled}
                experiments={experiments}
                onCloseModal={handleOnCloseModal}
                bugherdUrl={bugherdUrl}
                apiKey={apiKey}
                javascriptInstalledOverride={!!javascriptInstalledOverride}
                hasInvitedMember={hasInvitedMember}
                skippedInviteMember={skippedInviteMember}
              />
            )}
          </App>
        </Provider>
      </ConfigProvider>
    </CurrentUserProvider>
  );
};

export default Kanban;
